import { useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { useRef, useEffect, useCallback } from 'react';
import { isLandscape } from 'components/BottomDrawer/util';
import { PORTRAIT_MOBILE_PANEL_INITIAL_HEIGHT } from 'components/BottomDrawer/constants';
import { useApplicationStore } from 'modules/application/store';
import { dispatcher } from 'modules/editor/events';

const HEADER_HEIGHT = 100;

const getPixelToCameraUnitCoefficient = (camera: THREE.Camera) => {
  const y1 = 0; // Top of the screen in pixels
  const y2 = window.innerHeight; // Bottom of the screen in pixels

  // Convert screen coordinates to Normalized Device Coordinates
  const ndc1 = new THREE.Vector3(0, (y1 / window.innerHeight) * 2 - 1, 0.5);
  const ndc2 = new THREE.Vector3(0, (y2 / window.innerHeight) * 2 - 1, 0.5);

  // Unproject Normalized Device Coordinates to world coordinates
  const world1 = ndc1.clone().unproject(camera);
  const world2 = ndc2.clone().unproject(camera);

  // Calculate the world units difference
  const worldYDiff = world1.y - world2.y;

  // Calculate the coefficient (world units per pixel)
  const pixelDiff = y2 - y1;

  return worldYDiff / pixelDiff;
};

export const useMobileInitialCameraPosition = () => {
  const { camera } = useThree();
  const isMobile = useApplicationStore((state) => state.isMobile);
  const adjustedPosition = useRef(camera.position.clone());
  const canvasAspect = useApplicationStore((state) => state.canvasAspect);
  const changeCameraYPosition = useCallback(
    (y: number) => {
      if (camera.position.y !== y) {
        camera.position.setY(y);
        camera.updateProjectionMatrix();
        dispatcher.panChanged();
        adjustedPosition.current = camera.position.clone();
      }
    },
    [camera]
  );

  useEffect(() => {
    // ignore if not mobile or camera was already moved by user
    if (!isMobile || !camera.position.equals(adjustedPosition.current)) {
      return;
    }

    if (isLandscape()) {
      changeCameraYPosition(0);
      return;
    }

    const y = ((PORTRAIT_MOBILE_PANEL_INITIAL_HEIGHT - HEADER_HEIGHT) / 2) * getPixelToCameraUnitCoefficient(camera);
    changeCameraYPosition(y);
  }, [isMobile, changeCameraYPosition, camera, canvasAspect]);
};
